import { useEffect, useState } from "react";
import { portalApi, formatApiErrorDetail, nivelInfo } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PasswordInput } from "@/components/PasswordInput";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Loader2, ShieldCheck, Save, Plus, CalendarClock, XCircle, KeyRound } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";

const masUnDia = (iso) => {
  if (!iso) return "—";
  const d = new Date(`${iso}T12:00:00`);
  d.setDate(d.getDate() + 1);
  return d.toISOString().slice(0, 10);
};

export const PortalPerfil = ({ padre, hijos, onGuardado, onAnadirHijo }) => {
  const [form, setForm] = useState(null);
  const [guardando, setGuardando] = useState(false);
  const [baja, setBaja] = useState(null);
  const [motivo, setMotivo] = useState("");
  const [pass, setPass] = useState({ actual: "", nueva: "", repite: "" });

  const cambiarPassword = async () => {
    if (pass.nueva.length < 6) return toast.error("La nueva contraseña necesita al menos 6 caracteres");
    if (pass.nueva !== pass.repite) return toast.error("Las contraseñas nuevas no coinciden");
    try {
      await portalApi.post("/portal/password/cambiar", { actual: pass.actual, nueva: pass.nueva });
      toast.success("Contraseña actualizada");
      setPass({ actual: "", nueva: "", repite: "" });
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const solicitarBaja = async () => {
    if (motivo.trim().length < 3) return toast.error("Cuéntanos el motivo de la baja");
    try {
      const { data } = await portalApi.post("/portal/baja", { alumno_id: baja.id, motivo });
      toast.success(`Baja registrada. Las clases siguen activas hasta el ${data.hasta || "final del periodo"}.`);
      setBaja(null);
      setMotivo("");
      onGuardado?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  useEffect(() => {
    setForm({
      nombre: padre?.nombre || "", apellidos: padre?.apellidos || "", nif: padre?.nif || "",
      telefono: padre?.telefono || "", email: padre?.email || "", direccion: padre?.direccion || "",
      ciudad: padre?.ciudad || "", codigo_postal: padre?.codigo_postal || "",
      acepta_comunicaciones: !!padre?.acepta_comunicaciones,
    });
  }, [padre]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const guardar = async () => {
    setGuardando(true);
    try {
      await portalApi.put("/portal/me", form);
      toast.success("Datos actualizados");
      onGuardado?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setGuardando(false);
    }
  };

  if (!form) return <Loader2 className="h-5 w-5 animate-spin text-primary" />;

  return (
    <div data-testid="portal-perfil">
      <Card className="rounded-2xl p-6 sm:p-8 border-border shadow-sm">
        <h3 className="font-display text-xl font-bold mb-6">Mis datos</h3>
        <div className="grid sm:grid-cols-2 gap-4">
          <div className="grid gap-2">
            <Label>Nombre</Label>
            <Input value={form.nombre} onChange={(e) => set("nombre", e.target.value)} data-testid="perfil-nombre-input" />
          </div>
          <div className="grid gap-2">
            <Label>Apellidos</Label>
            <Input value={form.apellidos} onChange={(e) => set("apellidos", e.target.value)} data-testid="perfil-apellidos-input" />
          </div>
          <div className="grid gap-2">
            <Label>NIF / CIF</Label>
            <Input value={form.nif} onChange={(e) => set("nif", e.target.value)} data-testid="perfil-nif-input" />
          </div>
          <div className="grid gap-2">
            <Label>Teléfono</Label>
            <Input value={form.telefono} onChange={(e) => set("telefono", e.target.value)} data-testid="perfil-telefono-input" />
          </div>
          <div className="grid gap-2 sm:col-span-2">
            <Label>Email</Label>
            <Input type="email" value={form.email} onChange={(e) => set("email", e.target.value)} data-testid="perfil-email-input" />
          </div>
          <div className="grid gap-2 sm:col-span-2">
            <Label>Dirección</Label>
            <Input value={form.direccion} onChange={(e) => set("direccion", e.target.value)} data-testid="perfil-direccion-input" />
          </div>
          <div className="grid gap-2">
            <Label>Ciudad</Label>
            <Input value={form.ciudad} onChange={(e) => set("ciudad", e.target.value)} data-testid="perfil-ciudad-input" />
          </div>
          <div className="grid gap-2">
            <Label>Código postal</Label>
            <Input value={form.codigo_postal} onChange={(e) => set("codigo_postal", e.target.value)} data-testid="perfil-cp-input" />
          </div>
        </div>
        <div className="flex items-center justify-between rounded-lg border border-border p-4 mt-5">
          <div>
            <Label>Quiero recibir avisos y novedades de la academia</Label>
            <p className="text-xs text-muted-foreground mt-1">Puedes cambiarlo cuando quieras.</p>
          </div>
          <Switch checked={form.acepta_comunicaciones}
            onCheckedChange={(v) => set("acepta_comunicaciones", v)} data-testid="perfil-comunicaciones-switch" />
        </div>
        <Button className="rounded-full font-semibold mt-7" onClick={guardar} disabled={guardando} data-testid="guardar-perfil-btn">
          {guardando ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="h-4 w-4 mr-2" /> Guardar cambios</>}
        </Button>
      </Card>

      <Card className="rounded-2xl p-6 border-border shadow-sm mt-6" data-testid="card-password">
        <div className="flex items-center gap-2 mb-4">
          <KeyRound className="h-5 w-5 text-primary" />
          <h3 className="font-display text-lg font-bold">Cambiar mi contraseña</h3>
        </div>
        <div className="grid gap-4 sm:grid-cols-3">
          <div className="grid gap-2">
            <Label>Contraseña actual</Label>
            <PasswordInput value={pass.actual} onChange={(e) => setPass({ ...pass, actual: e.target.value })}
              data-testid="pass-actual-input" />
          </div>
          <div className="grid gap-2">
            <Label>Nueva contraseña</Label>
            <PasswordInput value={pass.nueva} onChange={(e) => setPass({ ...pass, nueva: e.target.value })}
              data-testid="pass-nueva-input" />
          </div>
          <div className="grid gap-2">
            <Label>Repite la nueva</Label>
            <PasswordInput value={pass.repite} onChange={(e) => setPass({ ...pass, repite: e.target.value })}
              data-testid="pass-repite-input" />
          </div>
        </div>
        <Button className="rounded-full font-semibold mt-6" onClick={cambiarPassword} data-testid="cambiar-pass-btn">
          Actualizar contraseña
        </Button>
      </Card>

      <div className="flex items-end justify-between gap-4 mt-10 mb-5">
        <h3 className="font-display text-xl font-bold">Mis hijos/as</h3>
        <Button variant="outline" className="rounded-full font-semibold" onClick={onAnadirHijo}
          data-testid="anadir-hijo-btn">
          <Plus className="h-4 w-4 mr-2" /> Añadir hijo/a
        </Button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {hijos.map((h) => {
          const nivel = nivelInfo(h.nivel);
          return (
            <Card key={h.id} className="rounded-2xl p-6 border-border shadow-sm" data-testid={`perfil-hijo-${h.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="font-display font-bold text-lg">{h.nombre} {h.apellidos}</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    Matrícula {h.matricula}{h.fecha_nacimiento ? ` · ${h.fecha_nacimiento}` : ""}
                  </p>
                </div>
                <Badge variant="outline" className="rounded-full gap-1.5">
                  <span className={`h-2 w-2 rounded-full ${nivel.dot}`} /> {nivel.label}
                </Badge>
              </div>
              <div className="mt-4 space-y-1.5 text-sm text-muted-foreground">
                <p>Sede: {h.sede || "—"}</p>
                <p className="flex items-center gap-1.5">
                  <ShieldCheck className={`h-3.5 w-3.5 ${h.seguro_reposicion ? "text-primary" : "text-muted-foreground"}`} />
                  {h.seguro_reposicion ? "Con seguro de recuperación" : "Sin seguro de recuperación"}
                </p>
                <p>Clases en bono: {h.clases_disponibles}</p>
                {(h.campamento_incluido || 0) > 0 && (
                  <p data-testid={`perfil-campamento-${h.id}`}>
                    Semana de campamento:{" "}
                    {(h.campamento_usos || []).length === 0
                      ? "pendiente de usar"
                      : (h.campamento_usos || []).map((u) => `del ${u.desde} al ${u.hasta}`).join(" · ")}
                  </p>
                )}
              </div>
              <div className="mt-5 rounded-xl border border-border bg-muted/40 p-4" data-testid={`suscripcion-${h.id}`}>
                <p className="flex items-center gap-1.5 text-xs uppercase tracking-wider font-semibold text-muted-foreground">
                  <CalendarClock className="h-3.5 w-3.5" /> Suscripción
                </p>
                <div className="mt-3 space-y-1.5 text-sm">
                  <p><span className="text-muted-foreground">Plan: </span>
                    {h.plan_nombre || "Sin plan contratado"}</p>
                  <p><span className="text-muted-foreground">Inicio: </span>
                    {h.inscripcion_desde || "—"}</p>
                  <p><span className="text-muted-foreground">Próxima renovación: </span>
                    {h.baja_solicitada ? "No se renovará" : masUnDia(h.inscripcion_hasta)}</p>
                  <p><span className="text-muted-foreground">Clases pagadas hasta: </span>
                    {h.inscripcion_hasta || "—"}</p>
                </div>
                {h.baja_solicitada ? (
                  <p className="text-xs text-destructive font-semibold mt-4" data-testid={`baja-hecha-${h.id}`}>
                    Baja solicitada. Sus clases terminan el {h.inscripcion_hasta || "final del periodo"}.
                  </p>
                ) : (h.stripe_subscription_id || h.inscripcion_hasta) ? (
                  <Button variant="ghost" size="sm" className="rounded-full mt-4 text-destructive"
                    onClick={() => { setBaja(h); setMotivo(""); }} data-testid={`baja-btn-${h.id}`}>
                    <XCircle className="h-3.5 w-3.5 mr-1.5" /> Darme de baja
                  </Button>
                ) : null}
              </div>

              <p className="text-xs text-muted-foreground mt-5">
                Para cambiar datos del alumno/a (nivel o clases) escribe a tu sede.
              </p>
            </Card>
          );
        })}
      </div>

      <Dialog open={!!baja} onOpenChange={(v) => !v && setBaja(null)}>
        <DialogContent className="rounded-2xl" data-testid="baja-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">Darse de baja</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            {baja?.nombre} seguirá viniendo a clase hasta el {baja?.inscripcion_hasta || "final del periodo pagado"} y
            no se hará ningún cobro más. ¿Nos cuentas el motivo?
          </p>
          <Textarea value={motivo} onChange={(e) => setMotivo(e.target.value)} rows={4}
            placeholder="Cambio de horarios, mudanza, motivos económicos..." data-testid="baja-motivo-input" />
          <DialogFooter>
            <Button variant="outline" className="rounded-full" onClick={() => setBaja(null)}
              data-testid="baja-cancelar-btn">Seguir en Bspark</Button>
            <Button variant="destructive" className="rounded-full font-semibold" onClick={solicitarBaja}
              data-testid="baja-confirmar-btn">Confirmar la baja</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
